import React from 'react';
import { supabase } from '../lib/supabase';
import { Database } from '../types/supabase';
import Navigation from '../components/Navigation';
import { Calendar, Plus, Clock, ExternalLink, ArrowRight, FileText, Bell } from 'lucide-react';
import { toast } from '../lib/toast';
import ReminderSection from '../components/ReminderSection';
import DashboardStats from '../components/DashboardStats';
import DashboardHighlights from '../components/DashboardHighlights';
import CalendarWidget from '../components/CalendarWidget';
import { useEffect, useState } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

type Agency = Database['public']['Tables']['agencies']['Row'];
type Project = Database['public']['Tables']['projects']['Row'];
type Task = Database['public']['Tables']['tasks']['Row'];
type Note = Database['public']['Tables']['notes']['Row'];

interface DashboardSection {
  id: string;
  title: string;
  component: React.ReactNode;
}

interface SortableSectionProps {
  id: string;
  title: string;
  children: React.ReactNode;
}

function SortableSection({ id, title, children }: SortableSectionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-8 cursor-move hover:shadow-lg transition-shadow duration-200"
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
}

export default function Overview({ navigate }: { navigate: (to: string) => void }) {
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sections, setSections] = useState<DashboardSection[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const getSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await loadData();
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Auth error:', error);
        setLoading(false);
      }
    };
    getSession();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        loadData();
      } else if (event === 'SIGNED_OUT') {
        setAgencies([]);
        setProjects([]);
        setTasks([]);
        setNotes([]);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    // Initialize dashboard sections after data is loaded
    if (!loading && !error) {
      setSections([
        {
          id: 'stats',
          title: 'Statistics',
          component: <DashboardStats agencies={agencies} projects={projects} tasks={tasks} />
        },
        {
          id: 'highlights',
          title: 'Activity Highlights',
          component: <DashboardHighlights agencies={agencies} projects={projects} tasks={tasks} />
        },
        {
          id: 'reminders',
          title: 'Reminders',
          component: <ReminderSection notes={notes} navigate={navigate} />
        },
        {
          id: 'calendar',
          title: 'Calendar',
          component: <CalendarWidget />
        }
      ]);
    }
  }, [loading, error, agencies, projects, tasks, notes]);

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setSections((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        
        // Save order to localStorage
        const newOrder = arrayMove(items, oldIndex, newIndex);
        localStorage.setItem('dashboardOrder', JSON.stringify(newOrder.map(item => item.id)));
        
        return newOrder;
      });
    }
  };

  const loadData = async () => {
    if (!loading) setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setLoading(false);
        setTasks([]);
        setAgencies([]);
        setProjects([]);
        setNotes([]);
        return;
      }

      let retries = 0;
      const maxRetries = 3;
      const delay = 1000;

      while (retries < maxRetries) {
        try {
          const [
            agenciesResult,
            projectsResult,
            tasksResult,
            notesResult
          ] = await Promise.all([
            supabase
              .from('agencies')
              .select('*')
              .eq('user_id', session.user.id)
              .order('created_at', { ascending: false }),
            supabase
              .from('projects')
              .select('*')
              .eq('user_id', session.user.id)
              .order('created_at', { ascending: false }),
            supabase
              .from('tasks')
              .select('*')
              .eq('user_id', session.user.id)
              .order('created_at', { ascending: false }),
            supabase
              .from('notes')
              .select('*')
              .eq('user_id', session.user.id)
              .order('created_at', { ascending: false })
          ]);

          // Check for errors
          const errors = [
            agenciesResult.error,
            projectsResult.error,
            tasksResult.error,
            notesResult.error
          ].filter(Boolean);

          if (errors.length > 0) {
            throw new Error(errors[0]?.message || 'Failed to load data');
          }

          setAgencies(agenciesResult.data || []);
          setProjects(projectsResult.data || []);
          setTasks(tasksResult.data || []);
          setNotes(notesResult.data || []);

          return;
        } catch (error) {
          retries++;
          if (retries === maxRetries) throw error;
          await new Promise(resolve => setTimeout(resolve, delay * retries));
          continue;
        }
      }
      throw new Error('Failed to load data after retries');
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load dashboard data';
      setError(errorMessage);
      toast.error(errorMessage);
      // Set empty arrays to prevent undefined errors
      setAgencies([]);
      setProjects([]);
      setTasks([]);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={tasks}
        currentView="all"
        onViewChange={() => {}}
        navigate={navigate}
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0 bg-white">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Dashboard Overview</h1>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 dark:text-red-400">{error}</p>
              <button
                onClick={() => loadData()}
                className="mt-4 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* Stats Section */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={sections}
                  strategy={verticalListSortingStrategy}
                >
                  {sections.map((section) => (
                    <SortableSection
                      key={section.id}
                      id={section.id}
                      title={section.title}
                    >
                      {section.component}
                    </SortableSection>
                  ))}
                </SortableContext>
              </DndContext>
            </>
          )}
        </div>
      </main>
    </div>
  );
}